import { Injectable } from '@angular/core';
import { StoreConfig, Store, selectPersistStateInit } from '@datorama/akita';
import { navigation } from '../../navigation';
import { NavigationItem, UiColor } from '../../reference-data.service';
import { tap } from 'rxjs/operators';

export enum SidebarLocations {
  CALENDAR = 'calendar',
  EXECUTIVE_SUMMARY = 'executive-summary',
  ENCOUNTER_SUMMARY = 'encounter-summary',
  QUICK_NOTES = 'quick-notes',
  TASKS = 'tasks',
  WAITING_ROOM = 'waiting-room',
  PATHOLOGY = 'pathology',
  HIDDEN = 'hidden',
  NOTIFICATIONS = 'notifications',
  USER_FEEDBACK = 'user-feedback',
  NORA_CONSULT_SUMMARY = 'nora-consult-summary',
  ILAB_SANDBOX = 'ilab-sandbox',
  WHATSAPP_ACTION_CENTRE = 'whatsapp-action-centre',
  PATIENT_TIMELINE = 'patient-timeline',
  EXPERIMENTS = 'experiments',
}

export enum BrowserTypes {
  Web = 'web',
  Tablet = 'tablet',
  Mobile = 'mobile',
}

export enum BrowserSizeType {
  SMALL = 768, // mobile
  MEDIUM = 1280,
  LARGE = 1600,
  EXTRA_LARGE = 9999,
}

export interface UiState {
  displayProgressBar: boolean;
  displayProgressSpinner: boolean;
  pageVisible: boolean;
  leftPanelOpened: boolean;
  lastPatientUpdated: Date;
  online: boolean;
  currentPatientId: string;
  currentPatientTabs: ActivePatient[];
  browserType: BrowserTypes;
  sidebarLocation: SidebarLocations;
  browserWidth: number;
  browserHeight: number;
  practiceProviders: PracticeProviderStateUI[];
  navigation: NavigationItem[];
  consultTargetTab?: number;
  guidedTour: GuidedTour;
  selectedPathologyCentreTab: number;
  indexedConsultTabs?: IndexedConsultations;
  taskFilters: Record<string, string[]>;
  noraRecorderVisible: boolean;
  noraSubscriptionLevel: string | null;
}

export interface ActivePatient {
  patientId: string;
  patientName: string;
  loading?: boolean;
}

export interface PracticeProviderStateUI {
  providerId: string;
  hpcsaNo: string;
  doctorName: string;
  color: UiColor;
  selectedInCalendar: boolean;
  selectedInWaitingRoom: boolean;
}

export interface GuidedTour {
  id: string;
  status: 'inprogress' | 'paused' | 'completed';
  activeTab?: number;
}

export interface IndexedConsultations {
  [key: string]: ConsultTab;
}
export interface ConsultTab {
  selectedTab: number;
  selectedSubTab: number;
  scrollHeight: number;
}

export function createInitialState(): UiState {
  return {
    displayProgressBar: false,
    displayProgressSpinner: false,
    pageVisible: true,
    leftPanelOpened: true,
    lastPatientUpdated: null,
    online: true,
    currentPatientId: null,
    currentPatientTabs: [],
    browserType: getBrowserType(),
    sidebarLocation: SidebarLocations.HIDDEN,
    browserWidth: window.innerWidth,
    browserHeight: window.innerHeight,
    practiceProviders: [],
    consultTargetTab: null,
    guidedTour: null,
    navigation,
    selectedPathologyCentreTab: 0,
    indexedConsultTabs: null,
    taskFilters: {},
    noraRecorderVisible: false,
    noraSubscriptionLevel: null,
  };
}

export function getBrowserType() {
  const currentWidth = window.innerWidth;
  const currentHeight = window.innerHeight;
  const isMobile = currentWidth < BrowserSizeType.SMALL;
  const isTablet = currentWidth < BrowserSizeType.MEDIUM;
  if (isMobile) {
    return BrowserTypes.Mobile;
  }
  if (isTablet) {
    return BrowserTypes.Tablet;
  }
  return BrowserTypes.Web;
}

@Injectable({
  providedIn: 'root',
})
@StoreConfig({ name: 'ui-state', resettable: true })
export class UiStateStore extends Store<UiState> {
  constructor() {
    super(createInitialState());
    // make sure to NOT get infinite spinner on start if previous session
    // was interrupted during showing spinner and persisted state will show spinner again
    selectPersistStateInit()
      .pipe(
        tap(() => {
          this.update({ displayProgressSpinner: false, displayProgressBar: false });

          const currentState = this.getValue();
          if (currentState.navigation[0].children.length !== navigation[0].children.length) {
            this.update({
              navigation,
            });
          }
        })
      )
      .subscribe();
  }
}
